import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
  CardActions,
  Switch,
  Snackbar,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  SelectChangeEvent,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CurrencyService, { CurrencyRate } from '../../../services/CurrencyService';
import { useAuth } from '../../../contexts/AuthContext';
import { accountingApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';
import {
  CURRENCY_PAGE_EVENT,
  consumePendingCurrencyPage,
  type CurrencyPageRequest,
} from '../../aichat/agent/uiBridge';
import GuideButton from '../../common/guide/GuideButton';

// Kaydırılabilir ana içerik için styled component
const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto',
  height: 'calc(100vh - 64px)', // Header yüksekliğini çıkarıyoruz
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
}));

// Başlık alanı için styled component
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

// Para birimi kartı için styled component - daha da kompakt hale getirildi
const CurrencyCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  transition: 'all 0.1s',
  '&:hover': {
    boxShadow: theme.shadows[2],
    transform: 'translateY(-1px)'
  }
}));

// Çok küçük CardContent için styled component
const CompactCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(1),
  '&:last-child': {
    paddingBottom: theme.spacing(1),
  }
}));

// Çok küçük CardActions için styled component
const CompactCardActions = styled(CardActions)(({ theme }) => ({
  padding: theme.spacing(0, 0.5),
  minHeight: 'auto'
}));

interface CurrencySettings {
  [code: string]: boolean;
}

const Currency: React.FC = () => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const localizeCurrencyName = (name: string) => {
    if (isTr) return name;
    const map: Record<string, string> = {
      'TÜRK LİRASI': 'TURKISH LIRA',
      'ABD DOLARI': 'US DOLLAR',
      'AVUSTRALYA DOLARI': 'AUSTRALIAN DOLLAR',
      'DANİMARKA KRONU': 'DANISH KRONE',
      'EURO': 'EURO',
      'İNGİLİZ STERLİNİ': 'BRITISH POUND',
      'İSVİÇRE FRANGI': 'SWISS FRANC',
      'İSVEÇ KRONU': 'SWEDISH KRONA',
      'KANADA DOLARI': 'CANADIAN DOLLAR',
      'KUVEYT DİNARI': 'KUWAITI DINAR',
      'NORVEÇ KRONU': 'NORWEGIAN KRONE',
      'SUUDİ ARABİSTAN RİYALİ': 'SAUDI RIYAL',
      'JAPON YENİ': 'JAPANESE YEN',
      'RUMEN LEYİ': 'ROMANIAN LEU',
      'RUS RUBLESİ': 'RUSSIAN RUBLE',
      'ÇİN YUANI': 'CHINESE YUAN',
      'PAKİSTAN RUPİSİ': 'PAKISTANI RUPEE',
      'KATAR RİYALİ': 'QATARI RIYAL',
      'GÜNEY KORE WONU': 'SOUTH KOREAN WON',
      'AZERBAYCAN YENİ MANATI': 'AZERBAIJANI MANAT',
      'BİRLEŞİK ARAP EMİRLİKLERİ DİRHEMİ': 'UAE DIRHAM',
      'KAZAKİSTAN TENGESİ': 'KAZAKHSTANI TENGE',
      'ÖZEL ÇEKME HAKKI (SDR)': 'SPECIAL DRAWING RIGHT (SDR)',
    };
    return map[name] || name;
  };

  // AuthContext'ten companyId al
  const { userDetails } = useAuth();
  const authCompanyId = userDetails?.companyId || null;
  
  const [rates, setRates] = useState<CurrencyRate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [currencySettings, setCurrencySettings] = useState<CurrencySettings>({
    TRY: true // TL varsayılan olarak aktif
  });
  const [savingSettings, setSavingSettings] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');

  // Döviz çevirici için state'ler
  const [amount, setAmount] = useState<string>('');
  const [fromCurrency, setFromCurrency] = useState<string>('TRY');
  const [toCurrency, setToCurrency] = useState<string>('TRY'); // Başlangıçta TRY (her zaman aktif)
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const [conversionError, setConversionError] = useState<string | null>(null);
  const [rateType, setRateType] = useState<'buying' | 'selling'>('buying');
  const pendingAutoConvertRef = useRef(false);
  /** Ajan hedef para birimini verdiyse USD otomatik seçimini engelle */
  const agentLockedTargetRef = useRef(false);

  // Şirket para birimi ayarlarını SQL'den yükle
  useEffect(() => {
    const loadCurrencySettings = async () => {
      if (!authCompanyId) {
        console.log('⚠️ [CURRENCY] CompanyId henüz yüklenmedi');
        return;
      }
      
      try {
        console.log('🔄 [CURRENCY] SQL API\'den para birimi ayarları yükleniyor...');
        
        const response = await accountingApi.get(`/api/currency/settings`);
        
        const result = response.data;
        
        if (result.success && result.data) {
          console.log('✅ [CURRENCY] Para birimi ayarları yüklendi:', result.data);
          // TRY her zaman aktif olmalı
          setCurrencySettings({ ...result.data, TRY: true });
        } else {
          // Varsayılan ayarlar (sadece TRY aktif)
          setCurrencySettings({ TRY: true });
        }
      } catch (error) {
        console.error('❌ [CURRENCY] Ayarlar yüklenirken hata:', error);
        // Offline fallback
        setCurrencySettings({ TRY: true });
      }
    };
    
    loadCurrencySettings();
  }, [authCompanyId]);

  // Döviz kurlarını yükle
  const loadCurrencyRates = async () => {
    try {
      setLoading(true);
      setError(null);
      const currencyRates = await CurrencyService.fetchExchangeRates();
      setRates(currencyRates);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Döviz kurları yüklenirken hata:', error);
      setError(tt('Döviz kurları yüklenemedi. Lütfen daha sonra tekrar deneyin.', 'Currency rates could not be loaded. Please try again later.'));
    } finally {
      setLoading(false);
    }
  };

  // Sayfa yüklendiğinde döviz kurlarını al
  useEffect(() => {
    loadCurrencyRates();
  }, []);

  // AI ajanı: döviz sayfası açılınca çeviriciyi ön-doldur / kurları yenile
  useEffect(() => {
    const applyAgentRequest = (req: CurrencyPageRequest) => {
      if (!req) return;
      if (req.refreshRates) {
        void loadCurrencyRates();
      }
      if (req.amount !== undefined && req.amount !== null) {
        setAmount(String(req.amount));
        setConvertedAmount(null);
      }
      if (req.fromCurrency) setFromCurrency(String(req.fromCurrency).toUpperCase());
      if (req.toCurrency) {
        agentLockedTargetRef.current = true;
        setToCurrency(String(req.toCurrency).toUpperCase());
      }
      if (req.rateType === 'buying' || req.rateType === 'selling') {
        setRateType(req.rateType);
      }
      if (req.autoConvert === true) {
        pendingAutoConvertRef.current = true;
      }
    };

    const pending = consumePendingCurrencyPage();
    if (pending) applyAgentRequest(pending);

    const handler = (e: Event) => {
      consumePendingCurrencyPage();
      applyAgentRequest((e as CustomEvent<CurrencyPageRequest>).detail);
    };
    window.addEventListener(CURRENCY_PAGE_EVENT, handler);
    return () => window.removeEventListener(CURRENCY_PAGE_EVENT, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kurlar ve ayarlar yüklendiğinde USD'yi hedef para birimi olarak ayarla (eğer aktifse)
  useEffect(() => {
    if (agentLockedTargetRef.current) return;
    if (rates.length > 0 && toCurrency === 'TRY') {
      // USD aktif mi kontrol et
      const usdRate = rates.find(r => r.code === 'USD');
      if (usdRate && currencySettings['USD']) {
        console.log('💱 USD aktif, hedef para birimi olarak ayarlanıyor');
        setToCurrency('USD');
      } else if (usdRate && !currencySettings['USD']) {
        // USD aktif değilse, ilk aktif para birimini seç
        const firstActiveRate = rates.find(r => currencySettings[r.code]);
        if (firstActiveRate) {
          console.log(`💱 ${firstActiveRate.code} hedef para birimi olarak ayarlanıyor`);
          setToCurrency(firstActiveRate.code);
        }
      }
    }
  }, [rates, currencySettings, toCurrency]);

  // Yenileme işlemi
  const handleRefresh = () => {
    loadCurrencyRates();
  };

  // Para birimi durumunu değiştir
  const handleCurrencyToggle = (code: string) => {
    if (code === 'TRY') return; // TRY her zaman aktif, değiştirilemez
    
    setCurrencySettings(prev => ({
      ...prev,
      [code]: !prev[code]
    }));
  };

  // Para birimi ayarlarını SQL'e kaydet
  const handleSaveSettings = async () => {
    if (!authCompanyId) {
      setSnackbarMessage(tt('Şirket bilgisi bulunamadı, ayarlar kaydedilemedi.', 'Company information not found, settings could not be saved.'));
      setSnackbarOpen(true);
      return;
    }
    
    try {
      setSavingSettings(true);
      console.log('🔄 [CURRENCY] SQL API\'ye para birimi ayarları kaydediliyor...');
      
      const response = await accountingApi.post('/api/currency/settings', {
        Settings: currencySettings
      });
      
      const result = response.data;
      
      if (result.success) {
        console.log('✅ [CURRENCY] Para birimi ayarları kaydedildi');
        setSnackbarMessage(tt('Para birimi ayarları başarıyla kaydedildi.', 'Currency settings saved successfully.'));
      } else {
        console.error('❌ [CURRENCY] Kaydetme hatası:', result.message);
        setSnackbarMessage(result.message || tt('Para birimi ayarları kaydedilemedi.', 'Currency settings could not be saved.'));
      }
      
      setSnackbarOpen(true);
    } catch (error) {
      console.error('❌ [CURRENCY] Para birimi ayarları kaydedilirken hata:', error);
      setSnackbarMessage(tt('Para birimi ayarları kaydedilemedi.', 'Currency settings could not be saved.'));
      setSnackbarOpen(true);
    } finally {
      setSavingSettings(false);
    }
  };

  // Para birimlerini değiştir
  const handleSwapCurrencies = () => {
    const temp = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(temp);
    // Dönüşüm sonucunu sıfırla
    setConvertedAmount(null);
  };

  // Alış/Satış kuru seçimi değiştiğinde
  const handleRateTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRateType(event.target.value as 'buying' | 'selling');
    // Dönüşüm sonucunu sıfırla
    setConvertedAmount(null);
  };

  // Döviz çevirme işlemi
  const handleConvert = () => {
    try {
      setConversionError(null);
      const amountValue = parseFloat(amount);
      
      if (isNaN(amountValue) || amountValue <= 0) {
        setConversionError(tt('Lütfen geçerli bir miktar girin.', 'Please enter a valid amount.'));
        return;
      }
      
      // Kaynak ve hedef para birimi aynıysa
      if (fromCurrency === toCurrency) {
        setConvertedAmount(amountValue);
        return;
      }
      
      // Kurları bul
      const fromRate = fromCurrency === 'TRY' 
        ? { buying: 1, selling: 1 } 
        : rates.find(r => r.code === fromCurrency);
      
      const toRate = toCurrency === 'TRY' 
        ? { buying: 1, selling: 1 } 
        : rates.find(r => r.code === toCurrency);
      
      if (!fromRate || !toRate) {
        setConversionError(tt('Seçilen para birimleri için kur bilgisi bulunamadı.', 'Rate information not found for selected currencies.'));
        return;
      }
      
      // Seçilen kur tipine göre dönüşüm yap
      if (rateType === 'buying') {
        // Alış kurlarını kullanarak dönüşüm yap
        
        // TRY'den diğer para birimine (TRY -> X)
        if (fromCurrency === 'TRY') {
          // TRY'den diğer para birimine çevirirken, diğer para biriminin alış kuru kullanılır (TRY/X)
          const result = amountValue / toRate.buying;
          setConvertedAmount(result);
          return;
        }
        
        // Diğer para biriminden TRY'ye (X -> TRY)
        if (toCurrency === 'TRY') {
          // Diğer para biriminden TRY'ye çevirirken, diğer para biriminin alış kuru kullanılır (X/TRY)
          const result = amountValue * fromRate.buying;
          setConvertedAmount(result);
          return;
        }
        
        // Diğer para biriminden diğer para birimine (X -> Y)
        // Önce kaynak para birimini TRY'ye, sonra TRY'yi hedef para birimine çeviriyoruz
        const tryValue = amountValue * fromRate.buying; // X -> TRY
        const result = tryValue / toRate.buying; // TRY -> Y
        
        setConvertedAmount(result);
      } else {
        // Satış kurlarını kullanarak dönüşüm yap
        
        // TRY'den diğer para birimine (TRY -> X)
        if (fromCurrency === 'TRY') {
          // TRY'den diğer para birimine çevirirken, diğer para biriminin satış kuru kullanılır (TRY/X)
          const result = amountValue / toRate.selling;
          setConvertedAmount(result);
          return;
        }
        
        // Diğer para biriminden TRY'ye (X -> TRY)
        if (toCurrency === 'TRY') {
          // Diğer para biriminden TRY'ye çevirirken, diğer para biriminin satış kuru kullanılır (X/TRY)
          const result = amountValue * fromRate.selling;
          setConvertedAmount(result);
          return;
        }
        
        // Diğer para biriminden diğer para birimine (X -> Y)
        // Önce kaynak para birimini TRY'ye, sonra TRY'yi hedef para birimine çeviriyoruz
        const tryValue = amountValue * fromRate.selling; // X -> TRY
        const result = tryValue / toRate.selling; // TRY -> Y
        
        setConvertedAmount(result);
      }
    } catch (error) {
      console.error('Döviz çevirme hatası:', error);
      setConversionError(tt('Döviz çevirme işlemi sırasında bir hata oluştu.', 'An error occurred during currency conversion.'));
    }
  };

  // Ajan autoConvert: kurlar + tutar hazır olunca bir kez dönüştür
  useEffect(() => {
    if (!pendingAutoConvertRef.current) return;
    if (loading || rates.length === 0) return;
    if (!amount || amount === '0') return;
    pendingAutoConvertRef.current = false;
    handleConvert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, rates, amount, fromCurrency, toCurrency, rateType]);

  // Tarih formatla
  const formatDate = (date: Date | null) => {
    if (!date) return '-';
    return new Intl.DateTimeFormat(localeCode, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Para birimi formatla
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString(localeCode, {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4
    });
  };

  // Formatlanmış para birimini döndür
  const getFormattedCurrency = (amount: number, currencyCode: string) => {
    return amount.toLocaleString(localeCode, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }) + ' ' + CurrencyService.getCurrencySymbol(currencyCode);
  };

  // Snackbar kapat
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Aktif para birimlerini filtrele
  const getActiveCurrencies = () => {
    const result = [{ code: 'TRY', name: tt('Türk Lirası', 'Turkish Lira') }]; // TRY her zaman olacak
    
    // Diğer aktif para birimlerini ekle
    rates.forEach(rate => {
      if (currencySettings[rate.code]) {
        result.push({ code: rate.code, name: localizeCurrencyName(rate.name) });
      }
    });
    
    return result;
  };

  // Kaynak para birimi değiştirme
  const handleFromCurrencyChange = (event: SelectChangeEvent) => {
    setFromCurrency(event.target.value);
    // Dönüşüm sonucunu sıfırla
    setConvertedAmount(null);
  };

  // Hedef para birimi değiştirme
  const handleToCurrencyChange = (event: SelectChangeEvent) => {
    setToCurrency(event.target.value);
    // Dönüşüm sonucunu sıfırla
    setConvertedAmount(null);
  };

  // Miktar değiştirme
  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Sadece sayılar ve tek bir nokta kabul edilecek
    const value = event.target.value;
    const regex = /^[0-9]*\.?[0-9]*$/;
    
    if (value === '' || regex.test(value)) {
      setAmount(value);
      // Dönüşüm sonucunu sıfırla
      setConvertedAmount(null);
    }
  };

  return (
    <ScrollableContent>
      <HeaderContainer>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="h5" component="h1" fontWeight="bold">
            {tt('Döviz İşlemleri', 'Currency Operations')}
          </Typography>
          <GuideButton guideKey="finance.currency" />
        </Box>
        <Box>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<RefreshIcon />} 
            onClick={handleRefresh}
            disabled={loading}
            sx={{ mr: 1 }}
            size="small"
          >
            {tt('Yenile', 'Refresh')}
          </Button>
        </Box>
      </HeaderContainer>
      
      <Paper elevation={3} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6" fontSize="1rem">
            {tt('Para Birimi Ayarları', 'Currency Settings')}
          </Typography>
          <Button 
            variant="contained" 
            color="success" 
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            disabled={savingSettings}
            size="small"
          >
            {tt('Kaydet', 'Save')}
          </Button>
        </Box>
        
        <Typography variant="caption" color="text.secondary" paragraph>
          {tt('Şirketinizde kullanmak istediğiniz para birimlerini seçin. Seçilen para birimleri sisteminizde aktif olacaktır.', 'Select the currencies you want to use in your company. Selected currencies will be active in your system.')}
        </Typography>
        
        <Divider sx={{ mb: 1.5 }} />
        
        <Grid container spacing={1}>
          {/* TL para birimi kartı - her zaman aktif */}
          <Grid item xs={4} sm={3} md={2} lg={1.5}>
            <CurrencyCard elevation={1}>
              <CompactCardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" component="div" fontWeight="bold">
                    TRY
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                    {tt('TR Lirası', 'Turkish Lira')}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="center" my={0.5}>
                  <Typography variant="subtitle1">₺</Typography>
                </Box>
              </CompactCardContent>
              <CompactCardActions>
                <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                  {tt('Varsayılan', 'Default')}
                </Typography>
                <Box flexGrow={1} />
                <Switch 
                  checked={true} 
                  disabled={true}
                  inputProps={{ 'aria-label': tt('TRY aktif', 'TRY active') }}
                  size="small"
                />
              </CompactCardActions>
            </CurrencyCard>
          </Grid>
          
          {/* Diğer para birimleri */}
          {rates.map((rate) => (
            <Grid item xs={4} sm={3} md={2} lg={1.5} key={rate.code}>
              <CurrencyCard elevation={1}>
                <CompactCardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" component="div" fontWeight="bold">
                      {rate.code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontSize="0.65rem" noWrap>
                      {localizeCurrencyName(rate.name)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="center" my={0.5}>
                    <Typography variant="subtitle1">{CurrencyService.getCurrencySymbol(rate.code)}</Typography>
                  </Box>
                </CompactCardContent>
                <CompactCardActions>
                  <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                    {currencySettings[rate.code] ? tt('Aktif', 'Active') : tt('Pasif', 'Passive')}
                  </Typography>
                  <Box flexGrow={1} />
                  <Switch 
                    checked={Boolean(currencySettings[rate.code])} 
                    onChange={() => handleCurrencyToggle(rate.code)}
                    inputProps={{ 'aria-label': `${rate.code} toggle` }}
                    size="small"
                  />
                </CompactCardActions>
              </CurrencyCard>
            </Grid>
          ))}
        </Grid>
      </Paper>
      
      {/* Döviz Çevirici */}
      <Paper elevation={3} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6" fontSize="1rem">
            {tt('Döviz Çevirici', 'Currency Converter')}
          </Typography>
        </Box>
        
        <Typography variant="caption" color="text.secondary" paragraph>
          {tt('Para birimleri arasında dönüşüm yapmak için aşağıdaki aracı kullanabilirsiniz.', 'You can use the tool below to convert between currencies.')}
        </Typography>
        
        <Divider sx={{ mb: 1.5 }} />
        
        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField 
                fullWidth
                label={tt('Miktar', 'Amount')}
                value={amount}
                onChange={handleAmountChange}
                type="text"
                size="small"
                error={!!conversionError}
                helperText={conversionError}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <FormControl fullWidth size="small">
                  <InputLabel id="from-currency-label">{tt('Kaynak', 'From')}</InputLabel>
                  <Select
                    labelId="from-currency-label"
                    value={fromCurrency}
                    onChange={handleFromCurrencyChange}
                    label={tt('Kaynak', 'From')}
                  >
                    {getActiveCurrencies().map(currency => (
                      <MenuItem key={`from-${currency.code}`} value={currency.code}>
                        {currency.code} - {CurrencyService.getCurrencySymbol(currency.code)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <IconButton 
                  color="primary" 
                  onClick={handleSwapCurrencies}
                  size="small"
                >
                  <SwapHorizIcon />
                </IconButton>
                
                <FormControl fullWidth size="small">
                  <InputLabel id="to-currency-label">{tt('Hedef', 'To')}</InputLabel>
                  <Select
                    labelId="to-currency-label"
                    value={toCurrency}
                    onChange={handleToCurrencyChange}
                    label={tt('Hedef', 'To')}
                  >
                    {getActiveCurrencies().map(currency => (
                      <MenuItem key={`to-${currency.code}`} value={currency.code}>
                        {currency.code} - {CurrencyService.getCurrencySymbol(currency.code)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">{tt('Dönüşüm Kuru', 'Conversion Rate')}</FormLabel>
                <RadioGroup
                  row
                  name="rate-type"
                  value={rateType}
                  onChange={handleRateTypeChange}
                >
                  <FormControlLabel value="buying" control={<Radio size="small" />} label={tt('Alış Kuru', 'Buying Rate')} />
                  <FormControlLabel value="selling" control={<Radio size="small" />} label={tt('Satış Kuru', 'Selling Rate')} />
                </RadioGroup>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConvert}
                  disabled={!amount || amount === '0'}
                  size="small"
                >
                  {tt('Dönüştür', 'Convert')}
                </Button>
                
                {convertedAmount !== null && (
                  <Box sx={{ bgcolor: 'background.default', p: 1, borderRadius: 1, display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Typography variant="body2" component="div">
                      {parseFloat(amount).toLocaleString(localeCode)} {CurrencyService.getCurrencySymbol(fromCurrency)} = 
                    </Typography>
                    <Typography variant="body1" component="div" fontWeight="bold" color="primary.main" sx={{ ml: 1 }}>
                      {getFormattedCurrency(convertedAmount, toCurrency)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>
      
      <Paper elevation={3} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box display="flex" alignItems="center">
            <Typography variant="h6" fontSize="1rem" mr={1}>
              {tt('TCMB Güncel Döviz Kurları', 'CBRT Current Exchange Rates')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {tt('(Banknote efektif alış ile Banknote efektif satış değerleridir)', '(Values represent effective banknote buying and selling rates)')}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {tt('Son güncelleme', 'Last updated')}: {formatDate(lastUpdated)}
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 1.5 }} />
        
        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tt('Döviz Kodu', 'Currency Code')}</TableCell>
                  <TableCell>{tt('Döviz Adı', 'Currency Name')}</TableCell>
                  <TableCell align="right">{tt('Birim', 'Unit')}</TableCell>
                  <TableCell align="right">{tt('Alış', 'Buying')}</TableCell>
                  <TableCell align="right">{tt('Satış', 'Selling')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rates.map((rate) => (
                  <TableRow key={rate.code}>
                    <TableCell>{rate.code}</TableCell>
                    <TableCell>{localizeCurrencyName(rate.name)}</TableCell>
                    <TableCell align="right">{rate.unit}</TableCell>
                    <TableCell align="right">{formatCurrency(rate.buying)}</TableCell>
                    <TableCell align="right">{formatCurrency(rate.selling)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
      />
    </ScrollableContent>
  );
};

export default Currency; 